import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { StatusLabel, Text } from 'components/Text';
import { LoadingScreen } from 'components/LoadingScreen';
import { useParams, useNavigate } from 'react-router-dom';
import { GET_ORDER_BY_ID } from 'gql/queries';
import { Order } from 'types/order';
import { format } from 'date-fns';
import { useStore } from 'context/store-context';
import { Back } from 'components/Back';
import { formatCurrency, getStoreCurrencyFormatter } from 'utils';
import { withNotification } from 'components/Notification';
import { NotificationTypes } from 'utils';
import OrderRiskAnalysisResult from 'components/OrderRiskAnalysis/OrderRiskAnalysisResult';

import withConfirmation from 'hooks/withConfirmation';
import { ExternalLinkIcon, UserIcon } from '@heroicons/react/solid';
import { OrderItem } from 'components/OrderItem';
import { OrderStatus } from './data/orders-data';
import Panel from 'components/Panel';
import { Button } from 'components/Button';

const renderEmptyTrackingComponent = () => {
  return <Text value={'N/A'} type="body" className="text-gray-500" />;
};

const getFulFillmentStatus = (status: string | undefined | null) => {
  if (status === 'fulfilled') return 'FulFilled';
  else if (status === 'partial') return 'Partially Fulfilled';
  return 'Unfulfilled';
};

export const getOrderStatus = (order: Order) => {
  if (order.cancelledAt) {
    return OrderStatus.CANCELLED;
  } else if (order.claims.length) {
    return OrderStatus.CLAIM_FILED;
  } else if (order.protectionTotal > 0) {
    return OrderStatus.GUARANTEED;
  }
  return OrderStatus.NOT_GUARANTEED;
};

export const ViewOrder = withConfirmation(
  withNotification(({ showNotification, setShowConfirmation }: any) => {
    const [order, setOrder] = useState<Order | null>(null);
    const navigate = useNavigate();
    const params = useParams();

    const { storeId, storeProperties } = useStore();
    const { data, loading } = useQuery(GET_ORDER_BY_ID, {
      variables: {
        storeId,
        id: params.orderId,
      },
      onError(error) {
        showNotification(NotificationTypes.error, error?.message);
      },
    });

    const onFileClaim = (order: Order) => {
      navigate('/claims/new-claim', {
        state: { order, returnLocation: '/orders' },
      });
    };

    useEffect(() => {
      if (data?.orders) {
        setOrder(data.orders[0]);
      }
    }, [data]);

    if (loading) return <LoadingScreen />;

    const getOrderItemsTrackingLinks = (order: Order | undefined) => {
      if (!order || !order.items) {
        return renderEmptyTrackingComponent();
      }
      const itemsTrackingLink = order.items.filter(
        (item) => item.trackingUrl && item.trackingUrl !== '',
      );
      if (!itemsTrackingLink.length) {
        return renderEmptyTrackingComponent();
      }
      return itemsTrackingLink.map((item) => {
        return (
          <span key={item.title}>
            <a
              href={`${process.env.REACT_APP_TRACKING_PORTAL_URL}/${item.trackingId}`}
              target="_blank"
              rel="noreferrer"
            >
              <Text
                value={item.title}
                type="body"
                className="text-blue-500 underline"
              />
            </a>
          </span>
        );
      });
    };

    if (!order) return null;

    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pb-10">
        <div className="max-w-7xl mx-auto bg-white p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <Back className="mr-5" returnLocation={'/orders'} />
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-xl font-semibold text-gray-900">
                  {order.number}
                </h1>
                <ExternalLinkIcon className="w-4 h-4 text-gray-400" />
              </div>
              <div className="flex items-start gap-2 flex-col">
                <span
                  className={`px-2 py-1 ${
                    getOrderStatus(order).bgColor +
                    ' ' +
                    getOrderStatus(order).textColor
                  } text-sm rounded font-bold`}
                >
                  {getOrderStatus(order).label}
                </span>
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-gray-500">
                    Order created at{' '}
                    {format(new Date(order?.createdAt), 'do MMM yyyy hh:mm')}
                  </span>
                  {order.cancelledAt && (
                    <span className="text-sm text-gray-500">
                      Order cancelled at at{' '}
                      {format(
                        new Date(order?.cancelledAt),
                        'do MMM yyyy hh:mm',
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-end gap-2">
                {getOrderStatus(order).label === OrderStatus.GUARANTEED.label &&
                  !order?.claims?.length && (
                    <Button
                      props={{
                        'aria-label': 'File Claim',
                      }}
                      onClick={onFileClaim.bind(null, order)}
                    >
                      File Claim
                    </Button>
                  )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content - Left */}
            <div className="lg:col-span-2 space-y-6">
              {[
                OrderStatus.GUARANTEED.label,
                OrderStatus.CLAIM_FILED.label,
              ].includes(getOrderStatus(order).label) && (
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">
                      Risk Analysis
                    </h3>
                    <UserIcon className="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600" />
                  </div>

                  <div className="space-y-3">
                    {order.id && (
                      <OrderRiskAnalysisResult orderId={order.id} flex="row" />
                    )}
                  </div>
                </div>
              )}
              <Panel title="Order Items">
                <div className="px-3 pb-3">
                  {order.items.map((item) => (
                    <OrderItem
                      key={item.id}
                      order={item}
                      onRemoveItem={() => {}}
                      isDisplay
                      viewOrder
                    />
                  ))}
                </div>
              </Panel>
            </div>

            {/* Sidebar - Right */}
            <div className="space-y-6">
              {/* Customer */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Customer</h3>
                  <UserIcon className="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600" />
                </div>

                <div className="space-y-3">
                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Order Number
                    </div>
                    <div className="text-sm text-gray-500">{order.number}</div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Order Date
                    </div>
                    <div className="text-sm text-gray-500">
                      {format(new Date(order?.createdAt), 'do MMM yyyy hh:mm')}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Customer Name
                    </div>
                    <div className="text-sm text-gray-500">
                      {order.customerName}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Email
                    </div>
                    <div className="text-sm text-gray-500">
                      {order.customerEmail ?? 'N/A'}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Phone
                    </div>
                    <div className="text-sm text-gray-500">
                      {order.customerPhone ?? 'N/A'}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Order Total
                    </div>
                    <div className="text-sm text-gray-500">
                      {getStoreCurrencyFormatter(
                        storeProperties?.currency,
                        formatCurrency(order.total ?? 0),
                      )}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Guarantee Total
                    </div>
                    <div className="text-sm text-gray-500">
                      {getStoreCurrencyFormatter(
                        storeProperties?.currency,
                        order.total,
                      )}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700 mb-2">
                      Fulfillment Status
                    </div>
                    <StatusLabel
                      label={getFulFillmentStatus(order?.fulfillmentStatus)}
                      type={
                        getFulFillmentStatus(order?.fulfillmentStatus) ===
                        'FulFilled'
                          ? 'success'
                          : getFulFillmentStatus(order?.fulfillmentStatus) ===
                            'Partially Fulfilled'
                          ? 'warning'
                          : 'error'
                      }
                    />
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Tracking
                    </div>
                    <div className="text-sm text-gray-500">
                      {getOrderItemsTrackingLinks(order)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }),
);
